import get from 'lodash/get';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Helmet} from 'react-helmet-async';
import {connect} from 'react-redux';
import {compose} from 'redux';
import Portal from '@spothero/ui/portal';
import FacilityAPI from 'api/facility';
import GTMUtils from 'utils/gtm';
import withConfig from 'config/withConfig';
import SHLoader from 'common/loader/SHLoader';
import {getAirportTravelDistance, updateSelected} from 'spot/spot-actions';
import {fetchRates} from 'checkout/checkout-actions';
import {push} from 'router/router-actions';
import {updateTimes} from 'search-request/search-request-actions';
import trackAvailabilityCheck from 'segment/events/availability-check';
import trackSpotDetails from 'segment/events/spot-details';
import ExperimentUtils from 'utils/experiment';
import SHUtils from 'utils/sh-utils';
import SpotImages from 'spot-details/details/SpotImages';
import ShuttleInformation from './shuttle/ShuttleInformation';
import BenefitMessage from './header/BenefitMessage';
import SpotDetailsHeader from './header/SpotDetailsHeader';
import FacilityLocation from './FacilityLocation';
import SafetyAmenities from './SafetyAmenities';
import ThingsYouShouldKnow from '../details/ThingsYouShouldKnow';
import HowToRedeemArrivalDeparture from './HowToRedeemArrivalDeparture';
import FAQPanels from 'spot-details/footer/FAQPanels';
import TrustSealValueProps from 'spot-details/footer/TrustSealValueProps';
import AirportPurchaseInformation from './purchase/AirportPurchaseInformation';
import AirportPurchaseSnapshot from './purchase/AirportPurchaseSnapshot';
import ConfirmDateTimeModal from './purchase/ConfirmDateTimeModal';
import AccessHours from 'spot-details/access/AccessHours';

class SpotDetailsAirport extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        match: PropTypes.shape({
            params: PropTypes.shape({
                spotId: PropTypes.string,
            }),
        }).isRequired,
        user: PropTypes.object,
        city: PropTypes.object,
        destination: PropTypes.object,
        rateSessionId: PropTypes.string,
        currencyType: PropTypes.string,
        spot: PropTypes.object,
        isMonthly: PropTypes.bool,
        starts: PropTypes.string,
        ends: PropTypes.string,
        referrer: PropTypes.string,
        referrerAffiliate: PropTypes.string,
        updateSelectedSpot: PropTypes.func,
        updateSearchTimes: PropTypes.func,
        getSpotToAirportTravelDistance: PropTypes.func,
        fetchAirportRates: PropTypes.func,
        isLoading: PropTypes.bool,
        pushTo: PropTypes.func,
        isAirportRateUnavailable: PropTypes.bool,
    };
    _lastActionType = 'SEARCH';
    _isAirportDailyPriceEnabled = ExperimentUtils.isFeatureEnabled(
        'airport_sd_reservation_summary_price'
    );
    _isAirportShortenedDatepickerEnabled = ExperimentUtils.isFeatureEnabled(
        'airport_datepicker_test'
    );
    _bodyClasses = SHUtils.determineInitialLoadBodyClasses({
        isAirport: true,
        pageType: 'facility',
    });
    state = {
        showConfirmTimeModal: false,
        confirmTimeModalEditMode: false,
        confirmTimeModalErrorMessage: null,
        userEditedDateTime: false,
    };

    componentDidMount() {
        const {
            match: {
                params: {spotId},
            },
            starts,
            ends,
            fetchAirportRates,
        } = this.props;

        fetchAirportRates({
            spotId: parseInt(spotId, 10),
            starts,
            ends,
            airport: true,
            meta: {
                dateTimeChanged: true,
            },
        });

        GTMUtils.push({
            event: 'pageview',
            page: 'spotdetail-airport',
        });

        SHUtils.staticScrollTo();
    }

    componentDidUpdate(prevProps) {
        const {
            rateSessionId,
            user: {id: userId, status: userStatus},
            isMonthly: monthly,
            starts,
            ends,
            referrer,
            referrerAffiliate,
            city,
            destination,
            spot,
            getSpotToAirportTravelDistance,
        } = this.props;
        const {spot: prevSpot, rateSessionId: prevRateSessionId} = prevProps;

        if (!prevSpot && spot) {
            getSpotToAirportTravelDistance(spot).catch(error => {
                // silently catch this error to ignore errors being logged
            });

            trackSpotDetails({
                userId,
                city,
                destination,
                monthly,
                starts,
                ends,
                spot,
                referrer,
                referrerAffiliate,
            });
        }

        if (prevRateSessionId !== rateSessionId) {
            // a new rate load was initiated which signifies an availability check
            trackAvailabilityCheck({
                searchRequest: {
                    monthly,
                    starts,
                    ends,
                },
                city,
                spot,
                userStatus,
                screen: 'spot details',
                actionType: this._lastActionType,
            });
        }
    }

    _onApplyDateTimeChange = ({starts, ends}) => {
        const {
            spot: {
                facility: {id: spotId},
            },
            fetchAirportRates,
        } = this.props;

        this.setState({
            showConfirmTimeModal: false,
            confirmTimeModalEditMode: false,
            userEditedDateTime: true,
            confirmTimeModalErrorMessage: null,
        });

        this._lastActionType = 'UPDATE_REQUESTED_TIMES';

        fetchAirportRates({
            spotId,
            starts,
            ends,
            airport: true,
            meta: {
                dateTimeChanged: true,
            },
        });
    };

    _onConfirmDateTime = ({starts, ends}) => {
        const {
            spot: {
                facility: {id: spotId, slug},
            },
            pushTo,
        } = this.props;

        // Run a pre-check in case date/times is no longer available
        FacilityAPI.getRates({
            spotId,
            starts,
            ends,
            airport: true,
        })
            .then(response => {
                const isNewRateAvailable =
                    get(response, 'data.data.hourly_rates', []).length > 0;

                if (isNewRateAvailable) {
                    // construct the checkout url on the fly
                    const newCheckoutUrl = `/checkout/${spotId}/${slug}/?starts=${starts}&ends=${ends}&airport=true`;

                    pushTo(newCheckoutUrl);
                } else {
                    this.setState({
                        confirmTimeModalErrorMessage:
                            'Sorry, the facility is sold out during that time, please try a different time.',
                    });
                }
            })
            .catch(error => {
                this.setState({
                    confirmTimeModalErrorMessage:
                        'An error occurred. Please try again.',
                });
            });
    };

    _onHideConfirmDateTimeModal = () => {
        this.setState({
            showConfirmTimeModal: false,
            confirmTimeModalEditMode: false,
            confirmTimeModalErrorMessage: null,
        });
    };

    _onShowConfirmDateTimeModal = isEditMode => {
        this.setState({
            showConfirmTimeModal: true,
            confirmTimeModalEditMode: isEditMode || false,
        });
    };

    _onCheckoutClick = () => {
        const {userEditedDateTime} = this.state;

        if (userEditedDateTime || !this._isAirportShortenedDatepickerEnabled) {
            const {
                spot: {
                    selectedRate: {fullUrl},
                },
                pushTo,
            } = this.props;

            pushTo(fullUrl);
        } else {
            this._onShowConfirmDateTimeModal();
        }
    };

    render() {
        const {
            isMobile,
            currencyType,
            spot,
            isMonthly,
            starts,
            ends,
            updateSearchTimes,
            fetchAirportRates,
            isLoading,
            pushTo,
            isAirportRateUnavailable,
            updateSelectedSpot,
        } = this.props;
        const {
            showConfirmTimeModal,
            confirmTimeModalEditMode,
            confirmTimeModalErrorMessage,
        } = this.state;
        let facility = null;
        let selectedRate = null;
        let distanceInMiles = null;
        let comparisons = null;

        if (spot) {
            facility = spot.facility;
            selectedRate = spot.selectedRate;
            distanceInMiles = spot.distanceInMiles;
            comparisons = spot.comparisons;
        }

        if (isLoading || !facility) {
            return <SHLoader className="SpotDetailsAirport-loader" />;
        }

        const {
            airport,
            cancellation_allowed: cancellationAllowed,
            amenities_full: amenities,
            rating_info: ratingInfo,
            images,
            title,
            available_transient_inventory: availableTransientInventory,
        } = facility;
        const {
            details: {code: iataCode},
            shuttle,
            lowest_daily_rate: lowestDailyRate,
        } = airport;
        const displayPrice = selectedRate
            ? selectedRate.display_price
            : lowestDailyRate;

        return (
            <div className="SpotDetailsAirport">
                <Helmet>
                    <meta name="robots" content="noindex, follow" />
                    <body className={this._bodyClasses} />
                </Helmet>
                <div className="SpotDetailsAirport-main-content">
                    <BenefitMessage
                        comparisons={comparisons}
                        iataCode={iataCode}
                        ratingInfo={ratingInfo}
                    />
                    <SpotImages
                        className="SpotDetailsAirport-header-carousel"
                        images={images}
                        alt={title}
                        height={335}
                        width={629}
                        showArrows={!isMobile}
                    />
                    <SpotDetailsHeader
                        facility={facility}
                        comparisons={comparisons}
                    />
                    <ShuttleInformation
                        shuttle={shuttle}
                        facilityTitle={title}
                        iataCode={iataCode}
                    />
                    <AccessHours
                        facility={facility}
                        rate={selectedRate}
                        sectionTitle="Facility Access Hours"
                    />
                    <FacilityLocation
                        facility={facility}
                        distanceInMiles={distanceInMiles}
                    />
                    <SafetyAmenities amenities={amenities} />
                    <ThingsYouShouldKnow
                        rate={selectedRate}
                        facility={facility}
                        isMonthly={isMonthly}
                        // eslint-disable-next-line no-empty-function
                        onShowRateDetailsClick={() => {}}
                    />
                    <HowToRedeemArrivalDeparture
                        selectedRate={selectedRate}
                        facility={facility}
                    />
                    <FAQPanels cancellationAllowed={cancellationAllowed} />
                    <TrustSealValueProps />
                </div>
                {isMobile ? (
                    <Portal>
                        <AirportPurchaseSnapshot
                            cancellationAllowed={cancellationAllowed}
                            availableTransientInventory={
                                availableTransientInventory
                            }
                            displayPrice={displayPrice}
                            currencyType={currencyType}
                            checkoutDisabled={!selectedRate}
                            onCheckoutClick={this._onCheckoutClick}
                        />
                    </Portal>
                ) : (
                    <div className="SpotDetailsAirport-checkout">
                        <AirportPurchaseInformation
                            onCheckoutClick={this._onCheckoutClick}
                            facility={facility}
                            selectedRate={selectedRate}
                            starts={starts}
                            ends={ends}
                            availableTransientInventory={
                                availableTransientInventory
                            }
                            fetchRates={fetchAirportRates}
                            updateSearchTimes={updateSearchTimes}
                            isAirportRateUnavailable={isAirportRateUnavailable}
                            isFetchingRates={isLoading}
                            pushTo={pushTo}
                            updateSelectedSpot={updateSelectedSpot}
                            showDailyPrice={this._isAirportDailyPriceEnabled}
                            displayPrice={displayPrice}
                            currencyType={currencyType}
                            isShortenedDatepickerExperiment={
                                this._isAirportShortenedDatepickerEnabled
                            }
                            onShowConfirmDateTimeModal={
                                this._onShowConfirmDateTimeModal
                            }
                        />
                    </div>
                )}
                {showConfirmTimeModal && (
                    <ConfirmDateTimeModal
                        starts={starts}
                        ends={ends}
                        isFetchingRates={isLoading}
                        isEditMode={confirmTimeModalEditMode}
                        onApply={this._onApplyDateTimeChange}
                        onConfirm={this._onConfirmDateTime}
                        onHidden={this._onHideConfirmDateTimeModal}
                        errorMessage={confirmTimeModalErrorMessage}
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {data: city},
        destination: {data: destination},
        checkout: {
            rateSessionId,
            currencyType,
            isFetchingRates,
            isAirportRateUnavailable,
            checkoutError,
        },
        search: {
            data: {referrerAffiliate},
        },
        searchRequest: {monthly, airport, starts, ends, referrer},
        spot: {selected},
        user: {data: user},
    } = state;

    return {
        isLoading: isFetchingRates,
        rateSessionId,
        checkoutError,
        user,
        city,
        destination,
        isMonthly: monthly,
        isAirport: airport,
        starts,
        ends,
        referrer,
        referrerAffiliate,
        spot: selected,
        currencyType,
        isAirportRateUnavailable,
    };
};

const mapDispatchToProps = {
    updateSelectedSpot: updateSelected,
    fetchAirportRates: fetchRates,
    getSpotToAirportTravelDistance: getAirportTravelDistance,
    updateSearchTimes: updateTimes,
    pushTo: push,
};

const enhance = compose(
    withConfig(['isMobile']),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(SpotDetailsAirport);
